import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import {
    Box, Button, ButtonGroup, Flex, Input, Select, Spinner, Td, Text,
    useColorModeValue, useToast
} from "@chakra-ui/react"
import { useQuery as useQueryHook } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Row, Table, Tag } from "components"
import { API, Enums } from "config"
import { useAllDistributors, useDistributorMovements } from "hooks"

// ─── Busca de produto (autocomplete inline) ───────────────────────────────────

const ProductSearch = ({ value, label, onSelect }) => {
    const [term, setTerm]               = useState(label || '')
    const [debouncedTerm, setDebounced] = useState('')
    const [open, setOpen]               = useState(false)
    const wrapRef = useRef()

    useEffect(() => {
        if (!term || term === label) return
        const t = setTimeout(() => setDebounced(term), 350)
        return () => clearTimeout(t)
    }, [term, label])

    const { data: products, isFetching } = useQueryHook({
        queryKey: ['product-search-movements', debouncedTerm],
        queryFn: async () => {
            const res = await API.get(`/products?term=${debouncedTerm}`)
            return Array.isArray(res.data) ? res.data : res.data?.data ?? []
        },
        enabled: debouncedTerm.length >= 2,
        staleTime: 30000,
    })

    const handleSelect = (p) => { onSelect(p); setTerm(p.name); setOpen(false) }
    const handleClear  = () => { onSelect(null); setTerm(''); setDebounced('') }

    if (value) {
        return (
            <Flex align="center" border="1.5px solid" borderColor="teal.300"
                bg="teal.50" _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                borderRadius="md" px={3} h="40px" gap={2}
            >
                <Icon name="product" boxSize={3.5} color="teal.500" />
                <Text fontSize="xs" flex={1} noOfLines={1} fontWeight="semibold"
                    color="teal.700" _dark={{ color: 'teal.200' }}>
                    {label || value}
                </Text>
                <IconButton icon="cancel" size="xs" variant="ghost" colorScheme="red"
                    aria-label="Remover produto" onClick={handleClear} />
            </Flex>
        )
    }

    return (
        <Box position="relative" ref={wrapRef}>
            <Input size="md" value={term} placeholder="Filtrar por produto..."
                onChange={e => { setTerm(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && term.length >= 2 && (
                <Box
                    position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" border="1px solid" borderColor="gray.200"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    borderRadius="md" boxShadow="lg" zIndex={1500} maxH="200px" overflowY="auto"
                >
                    {isFetching && <Flex justify="center" py={3}><Spinner size="sm" /></Flex>}
                    {!isFetching && (!products || products.length === 0) && (
                        <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }} px={4} py={3}>
                            Nenhum produto encontrado
                        </Text>
                    )}
                    {(products ?? []).slice(0, 10).map(p => (
                        <Box key={p.id} px={4} py={2} cursor="pointer"
                            _hover={{ bg: 'teal.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(p)}
                        >
                            <Text fontSize="sm" fontWeight="semibold">{p.name}</Text>
                            {p.cod && <Text fontSize="xs" color="gray.400">Cód: {p.cod}</Text>}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── Input de data nativo (retorna string YYYY-MM-DD sem conversão UTC) ───────

const DateInput = ({ label, value, onChange, ...props }) => {
    const borderColor = useColorModeValue('gray.200', 'gray.600')
    const bg          = useColorModeValue('white', 'gray.700')
    const color       = useColorModeValue('gray.800', 'gray.100')

    return (
        <Box>
            <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                letterSpacing="wider" color={useColorModeValue('gray.500', 'gray.400')} mb={1}>
                {label}
            </Text>
            <input
                type="date"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={{
                    height: '40px',
                    padding: '0 12px',
                    fontSize: '14px',
                    borderRadius: '6px',
                    border: `1px solid`,
                    borderColor: borderColor,
                    background: bg,
                    color: color,
                    width: '155px',
                    outline: 'none',
                }}
                {...props}
            />
        </Box>
    )
}

// ─── Página de Movimentações ──────────────────────────────────────────────────

export default function Movements() {
    const distributors = useAllDistributors()

    const [page, setPage]             = useState(1)
    const [distributorId, setDistributorId] = useState('')
    const [type, setType]             = useState('')
    const [productId, setProductId]   = useState('')
    const [productName, setProductName] = useState('')
    const [invoiceNumber, setInvoiceNumber] = useState('')
    const [lotNumber, setLotNumber]   = useState('')
    const [from, setFrom]             = useState('')
    const [to, setTo]                 = useState('')

    const filterBg     = useColorModeValue('gray.50',  'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor   = useColorModeValue('gray.500', 'gray.400')

    const hasFilters = !!(distributorId || type || productId || invoiceNumber || lotNumber || from || to)

    const filters = {
        ...(type          && { type }),
        ...(productId     && { product_id: productId }),
        ...(invoiceNumber && { invoice_number: invoiceNumber }),
        ...(lotNumber     && { lot_number: lotNumber }),
        ...(from          && { from }),
        ...(to            && { to }),
    }

    const movements = useDistributorMovements(distributorId, page, filters)

    useEffect(() => { setPage(1) }, [distributorId, type, productId, invoiceNumber, lotNumber, from, to])

    const handleProductSelect = (p) => {
        setProductId(p?.id ?? '')
        setProductName(p?.name ?? '')
    }

    const clearFilters = () => {
        setDistributorId('')
        setType('')
        setProductId('')
        setProductName('')
        setInvoiceNumber('')
        setLotNumber('')
        setFrom('')
        setTo('')
        setPage(1)
    }

    const typeOptions = [
        { label: 'Todos os tipos',          value: '' },
        { label: 'Entrada por NF-e',        value: Enums.StockMovementType.IN_PURCHASE       },
        { label: 'Liberação de Bloqueio',   value: Enums.StockMovementType.RELEASE_BLOCKED   },
        { label: 'Saída - Transferência',   value: Enums.StockMovementType.TRANSFER_OUT      },
        { label: 'Entrada - Transferência', value: Enums.StockMovementType.TRANSFER_IN       },
        { label: 'Perda',                   value: Enums.StockMovementType.LOSS              },
        { label: 'Ajuste Manual',           value: Enums.StockMovementType.MANUAL_ADJUSTMENT },
    ]

    const warehouseOptions = [
        { label: 'Selecione um núcleo', value: '' },
        ...(distributors.data?.filter(d => d.is_warehouse) ?? []).map(d => ({ label: d.name, value: d.id }))
    ]

    const columns = [
        { children: 'Tipo' },
        { children: 'Produto' },
        { children: 'Quantidade', isNumeric: true },
        { children: 'Origem / Destino' },
        { children: 'NF-e / Ref.' },
        { children: 'Lote' },
        { children: 'Funcionário' },
        { children: 'Data', isNumeric: true },
    ]

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Movimentações de Estoque">
                <ButtonGroup size="sm">
                    <IconButton
                        icon="refresh"
                        isLoading={movements.isFetching}
                        onClick={movements.refetch}
                        isDisabled={!distributorId}
                    />
                </ButtonGroup>
            </Heading>

            {/* Painel de filtros */}
            <Box
                mt={4} bg={filterBg} border="1px solid" borderColor={filterBorder}
                borderRadius="xl" p={4}
            >
                <Flex justify="space-between" align="center" mb={3}>
                    <Flex align="center" gap={2}>
                        <Icon name="search" boxSize={3.5} color={labelColor} />
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="widest" color={labelColor}>
                            Filtros
                        </Text>
                        {hasFilters && <Box w="6px" h="6px" borderRadius="full" bg="teal.400" />}
                    </Flex>
                    {hasFilters && (
                        <Button size="sm" colorScheme="red" variant="outline" onClick={clearFilters}
                            leftIcon={<Icon name="close" boxSize={3.5} />} borderRadius="full">
                            Limpar Filtros
                        </Button>
                    )}
                </Flex>

                <Flex gap={3} flexWrap="wrap" align="flex-end">
                    {/* Núcleo — obrigatório */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Núcleo *
                        </Text>
                        <Select size="md" w="240px" value={distributorId}
                            onChange={e => setDistributorId(e.target.value)}>
                            {warehouseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Select>
                    </Box>

                    {/* Tipo */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Tipo
                        </Text>
                        <Select size="md" w="220px" value={type}
                            onChange={e => setType(e.target.value)} isDisabled={!distributorId}>
                            {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Select>
                    </Box>

                    {/* Produto */}
                    <Box w="240px">
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Produto
                        </Text>
                        <ProductSearch value={productId} label={productName} onSelect={handleProductSelect} />
                    </Box>

                    {/* Número da nota */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Número da nota
                        </Text>
                        <Input size="md" w="160px" value={invoiceNumber}
                            placeholder="Ex: 001234"
                            fontFamily="mono"
                            isDisabled={!distributorId}
                            onChange={e => setInvoiceNumber(e.target.value)}
                        />
                    </Box>

                    {/* Número do lote */}
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Número do lote
                        </Text>
                        <Input size="md" w="160px" value={lotNumber}
                            placeholder="Ex: LOT-001"
                            fontFamily="mono"
                            isDisabled={!distributorId}
                            onChange={e => setLotNumber(e.target.value)}
                        />
                    </Box>

                    {/* Período — inputs nativos evitam conversão UTC */}
                    <DateInput label="De" value={from} onChange={setFrom} />
                    <DateInput label="Até" value={to} onChange={v => { setTo(v); }} />
                </Flex>
            </Box>

            {!distributorId ? (
                <Flex mt={12} justifyContent="center" direction="column" align="center" gap={2}>
                    <Icon name="movement" boxSize={8} color="gray.300" />
                    <Text color="GrayText" fontSize="sm">Selecione um núcleo para visualizar as movimentações.</Text>
                </Flex>
            ) : (
                <Table columns={columns} mt={4} setPage={setPage} meta={movements.data?.meta}>
                    {movements.data?.data?.map(item => (
                        <MovementRow key={item.id} data={item} />
                    ))}
                </Table>
            )}
        </Container>
    )
}

// ─── Linha de movimentação ────────────────────────────────────────────────────

const MovementRow = ({ data }) => {
    const navigate = useNavigate()
    const toast    = useToast()
    const type     = Enums.StockMovementType.data(data?.type)
    const isEntry  = data?.quantity > 0

    const copyNumber = (e, num) => {
        e.stopPropagation()
        navigator.clipboard.writeText(num).then(() => {
            toast({ title: `NF ${num} copiada`, status: 'info', duration: 1500, isClosable: true })
        })
    }

    return (
        <Row>
            <Td><Tag colorScheme={type.colorScheme} size="sm">{type.label}</Tag></Td>
            <Td>
                <Text fontSize="sm" fontWeight="medium">{data?.product?.name}</Text>
            </Td>
            <Td isNumeric>
                <Text fontWeight="bold" color={isEntry ? 'green.500' : 'red.500'} fontSize="sm">
                    {isEntry ? '+' : ''}{data?.quantity}
                </Text>
            </Td>
            <Td>
                <Flex direction="column" gap={0.5}>
                    {data?.origin_distributor?.name && (
                        <Text fontSize="xs" color="gray.500">
                            De: <Box as="span" fontWeight="medium" color="inherit">{data.origin_distributor.name}</Box>
                        </Text>
                    )}
                    {data?.destination_distributor?.name && (
                        <Text fontSize="xs" color="gray.500">
                            Para: <Box as="span" fontWeight="medium" color="inherit">{data.destination_distributor.name}</Box>
                        </Text>
                    )}
                    {!data?.origin_distributor?.name && !data?.destination_distributor?.name && (
                        <Text fontSize="xs" color="gray.400">—</Text>
                    )}
                </Flex>
            </Td>
            <Td>
                {data?.invoice ? (
                    <Flex align="center" gap={1.5}>
                        <Box
                            as="button"
                            onClick={() => navigate('/stock/invoices', { state: { search: data.invoice.number } })}
                            fontFamily="mono" fontSize="xs" fontWeight="semibold"
                            color="teal.500" _hover={{ color: 'teal.300', textDecoration: 'underline' }}
                            transition="color 0.15s"
                        >
                            NF #{data.invoice.number}
                        </Box>
                        <Box
                            as="button"
                            onClick={e => copyNumber(e, data.invoice.number)}
                            color="gray.400" _hover={{ color: 'teal.400' }}
                            lineHeight={0} transition="color 0.15s"
                            title="Copiar número"
                        >
                            <Icon name="copy" boxSize={3} />
                        </Box>
                    </Flex>
                ) : data?.notes ? (
                    <Text fontSize="xs" color="gray.400" noOfLines={1} maxW="140px">
                        {data.notes}
                    </Text>
                ) : (
                    <Text fontSize="xs" color="gray.400">—</Text>
                )}
            </Td>
            <Td>
                {data?.lot_number ? (
                    <Tag colorScheme="purple" size="sm" fontFamily="mono">{data.lot_number}</Tag>
                ) : (
                    <Text fontSize="xs" color="gray.400">—</Text>
                )}
            </Td>
            <Td>
                <Text fontSize="xs">{data?.employee?.name ?? '—'}</Text>
            </Td>
            <Td isNumeric whiteSpace="nowrap">
                <Text fontSize="xs">
                    {data?.created_at
                        ? new Date(data.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                </Text>
            </Td>
        </Row>
    )
}
